import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Typography,
  Card,
  Modal,
} from "antd";
import Select from "../../../shared/components/searchable-select.component";
import {
  MinusCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { RootState } from "../../../shared/types/common";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import { Client } from "../../../core/api-client";
import {
  API_GET_ALL_PROJECT_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_BILLING_DETAILS,
  API_GET_STAFF_DETAILS,
} from "../../../shared/routes/private.routes";
import { formatAmount, showNotification } from "../../../shared/utils/utils";
import {
  createBillingCycle,
  updateBillingCycleById,
} from "../../../core/store/reducers/billing-cycle.slice";
import dayjs from "dayjs";
const { Option } = Select;

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const CreateEditBillingCycleComponent: React.FC<Props> = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { user } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [selectedStaffDetails, setSelectedStaffDetails] = useState<any | null>(
    null
  );
  const [selectedStaffName, setSelectedStaffName] = useState<string | null>(
    null
  );
  const [totalCost, setTotalCost] = useState<number>(0);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity1, setSelectedEntity] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const onFinish = (values: any) => {
    let allocations: {
      projectId: any;
      percentAllocation: number;
      monthlyCost: number;
    }[] = [];
    let totalPercentage = 0;
    if (values?.allocations) {
      for (let i = 0; i < values.allocations.length; i++) {
        const percent = values.allocations[i]?.percentAllocation || 0;
        totalPercentage += percent;

        allocations[i] = {
          projectId: values.allocations[i]?.project,
          percentAllocation: percent,
          monthlyCost: values.allocations[i]?.monthlyCost,
        };
      }
    }

    // Check if totalPercentage exceeds 100
    if (totalPercentage > 100) {
      showNotification("Allocation percentages cannot exceed 100%", "warning");
      return;
    }

    const payload = {
      staffId: values?.staff,
      month: dayjs(values?.month).format("YYYY-MM"),
      status: values?.status,
      totalCost: values?.totalCost,
      allocation: allocations,
    };
    if (selectedEntity) {
      dispatch(
        updateBillingCycleById({
          billingId: selectedEntity?.billingId,
          body: payload,
        })
      ).then((val: any) => {
        if (val?.payload?.data && val?.payload?.data?.statusCode === 200) {
          showNotification(
            "Billing Cycle Details Updated Successfully",
            "success"
          );
          onSubmit();
        }
        if (val?.payload?.data && val?.payload?.data?.statusCode === 400) {
          if (val?.payload?.data?.message === "billingCycleAlreadyExist") {
            showNotification("Billing Cycle Exist", "warning");
          } else {
            showNotification(val?.payload?.data?.message, "warning");
          }
        }
      });
    } else {
      dispatch(createBillingCycle(payload)).then((val: any) => {
        if (val?.payload?.data && val?.payload?.data?.statusCode === 200) {
          showNotification("New Billing Cycle Created Successfully", "success");
          onSubmit();
        }
        if (val?.payload?.data && val?.payload?.data?.statusCode === 400) {
          if (val?.payload?.data?.message === "billingCycleAlreadyExist") {
            showNotification("Billing Cycle Exist", "warning");
          } else {
            showNotification(val?.payload?.data?.message, "warning");
          }
        }
      });
    }
  };

  const staffDetails = async (staffId: string) => {
    const response = await Client.get(`${API_GET_STAFF_DETAILS}/${staffId}`);
    if (response?.data?.data) {
      setSelectedStaffDetails(response?.data?.data);
      setSelectedStaffName(response?.data?.data[0]?.staffName || "");
    } else {
      setSelectedStaffDetails(null);
    }
  };

  const fetchProjectList = async () => {
    const response = await Client.get(API_GET_ALL_PROJECT_LIST);
    if (response?.data?.data) {
      setProjectList(response?.data?.data);
    } else {
      setProjectList([]);
    }
  };

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  useEffect(() => {
    if (selectedEntity) {
      console.log("selectedEntity", selectedEntity);
      let allocations: {
        project: any;
        percentAllocation: number;
        monthlyCost: number;
      }[] = [];
      if (selectedEntity?.allocations) {
        for (let i = 0; i < selectedEntity.allocations.length; i++) {
          allocations[i] = {
            project: selectedEntity.allocations[i]?.projectId,
            percentAllocation: selectedEntity.allocations[i]?.percentAllocation,
            monthlyCost: selectedEntity.allocations[i]?.monthlyCost,
          };
        }
      }
      form.setFieldsValue({
        staff: selectedEntity?.staffId,
        month: dayjs(selectedEntity?.month),
        totalCost: selectedEntity?.totalCost,
        status: selectedEntity?.status,
        allocations: allocations,
      });
      staffDetails(selectedEntity?.staffId);
    }
  }, [form, selectedEntity]);

  useEffect(() => {
    fetchStaffList();
    fetchProjectList();
  }, []);

  const dropdownOptions = staffList.map((staff) => ({
    value: staff?.staffId,
    label: staff?.staffName,
  }));
  let projectDropdownOptions = projectList.map((project) => ({
    value: project?.projectId,
    label: project?.projectName,
  }));

  const onFinishFailed = (_errorInfo: any) => {
    return;
  };

  const onValuesChange = (value: any, allValues: any) => {
    const { staff, month, allocations } = allValues;
    if (staff && month) {
      fetchBillingDetails(staff, dayjs(month).format("YYYY-MM"));
    }
  };

  const monthlyCostCalculation = (percentAllocation: number | null) => {
    if (selectedStaffDetails && percentAllocation !== null) {
      const ctc = Number(selectedStaffDetails[0].ctc) || 0;
      const monthlyCost = Math.round((ctc / 12) * (percentAllocation / 100));
      return monthlyCost;
    }
    return 0;
  };

  const renderAllocationFields = () => {
    return (
      <Form
        form={form}
        name="dynamic_form_complex"
        autoComplete="off"
        initialValues={{
          allocations: selectedEntity ? selectedEntity.allocations : [{}],
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={onValuesChange}
      >
        <Form.List name="allocations">
          {(fields, { add, remove }) => (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {fields.map((field, index) => (
                <Card size="small" key={field.key} style={{ marginBottom: 16 }}>
                  <div
                    style={{ display: "flex", gap: 16, alignItems: "center" }}
                  >
                    <Form.Item
                      label="Project"
                      name={[field.name, "project"]}
                      style={{ marginBottom: 0, flex: 1 }}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Select placeholder="Select Project">
                        {projectDropdownOptions.map((option) => (
                          <Option
                            key={option.value}
                            value={option.value}
                            disabled={(
                              form.getFieldValue(["allocations"]) || []
                            ).some(
                              (allocation: { project: any }, idx: number) =>
                                allocation &&
                                idx !== index &&
                                allocation.project === option.value
                            )}
                          >
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Percentage"
                      name={[field.name, "percentAllocation"]}
                      rules={[
                        {
                          type: "number",
                          min: 0,
                          max: 100,
                          message: "Percentage must be between 0 and 100",
                        },
                        { required: true, message: "Required" },
                      ]}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Percentage"
                        onChange={(value) => {
                          const fieldsValue = form.getFieldsValue();
                          const allocations = [...fieldsValue.allocations];
                          allocations[index].monthlyCost =
                            monthlyCostCalculation(
                              value !== null ? Number(value) : null
                            );
                          form.setFieldsValue({ allocations });
                          updateTotalCost(allocations);
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Monthly Cost"
                      name={[field.name, "monthlyCost"]}
                      rules={[
                        { required: true, message: "Required" },
                        { type: "number", min: 0 },
                      ]}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Monthly Cost"
                        disabled
                        step={ 0.01}
                        // formatter={(value) =>
                        //    value != null ? formatAmount(value) : value
                        // }
                        // parser={(value) =>
                        //   ele.isAmount && value != null
                        //     ? value.replace(/[^\d.-]/g, "") // strip commas & symbols
                        //     : value
                        // }
                      />
                    </Form.Item>
                    <Form.Item>
                      {fields.length > 1 ? (
                        <CloseOutlined
                          onClick={() => {
                            remove(field.name);
                          }}
                        />
                      ) : null}
                    </Form.Item>
                  </div>
                </Card>
              ))}
              <div style={{ paddingBottom: "10%" }}>
                <Button type="dashed" onClick={() => add()} block>
                  + Add Allocation
                </Button>
              </div>
            </div>
          )}
        </Form.List>
      </Form>
    );
  };

  const updateTotalCost = (allocations: any[]) => {
    let sum = 0;
    allocations.forEach((allocation) => {
      if (allocation.monthlyCost) {
        sum += allocation.monthlyCost;
      }
    });
    setTotalCost(sum);
    form.setFieldsValue({ totalCost: sum });
  };

  const fetchBillingDetails = async (staffId: string, month: string) => {
    if (selectedEntity) {
      setSelectedEntity(null);
      setCreateEditOpen(false);
    } else {
      const response = await Client.get(
        `${API_GET_BILLING_DETAILS}?staffId=${staffId}&month=${month}`
      );
      if (response?.data) {
        const billingDetails = response?.data[0];
        console.log(billingDetails);
        selectedEntity = {
          key: 0,
          staffId: billingDetails.staffId,
          billingId: billingDetails.billingId,
          month: billingDetails.month,
          totalCost: billingDetails.totalCost,
          status: billingDetails.status,
          allocations: billingDetails.allocations.map((allocation: any) => ({
            projectId: allocation.projectId,
            percentAllocation: allocation.percentAllocation,
            monthlyCost: allocation.monthlyCost,
          })),
        };
        console.log("selectedEntitycustom", selectedEntity);
        setSelectedEntity(selectedEntity);
        setEditModalVisible(true);
      }
    }
  };
  var colSpan = screenType.isPortrait ? 24 : 12;
  const formProps = {
    form: form,
    name: "creat-edit-billing-cycle",
    heading: selectedEntity
      ? "Edit Billing Cycle Details"
      : "Add New Billing Cycle",
    initialValues: { status: "ACTIVE" },
    formElements: [
      {
        name: "staff",
        label: "Staff Name",
        placeholder: "Staff Name",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: dropdownOptions,
        onChange: (value: string) => {
          staffDetails(value);
        },
        rules: [{ required: true, message: "Please enter Staff Name" }],
      },
      {
        name: "month",
        label: "Month",
        placeholder: "Month",
        type: "MonthPicker",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        // options: [
        //   { value: 0, label: "January" },
        //   { value: 1, label: "February" },
        //   { value: 2, label: "March" },
        //   { value: 3, label: "April" },
        //   { value: 4, label: "May" },
        //   { value: 5, label: "June" },
        //   { value: 6, label: "July" },
        //   { value: 7, label: "August" },
        //   { value: 8, label: "September" },
        //   { value: 9, label: "October" },
        //   { value: 10, label: "November" },
        //   { value: 11, label: "December" },
        // ],
        rules: [{ required: true, message: "Month Required" }],
      },
      {
        name: "totalCost",
        colSpan: 12,
        label: "Total Cost",
        placeholder: "Total Cost",
        type: "Input",
        allowClear: true,
        isAmount: true,
        disabled: true,
        prefix: "RM",
        rules: [{ required: true, message: "Please enter Total Cost" }],
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Status",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
        rules: [{ required: true, message: "Please enter Status" }],
      },
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: false,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: onFinishFailed,
    onValuesChange: onValuesChange,
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
  };

  const onEditConfirm = () => {
    setCreateEditOpen(true);
    setEditModalVisible(false);
    // onEditConfirmCancel();
  };

  const onEditConfirmCancel = () => {
    setEditModalVisible(false);
    onClose();
  };

  return (
    <>
      <Drawer
        maskClosable={false}
        title={<strong>{formProps.heading}</strong>}
        className="form-drawer"
        width={
          screenType.isPortrait
            ? "90%"
            : screenType.isLg || screenType.isXl
            ? 720
            : 500
        }
        onClose={onClose}
        visible={visible}
      >
        <GenericFormComponent {...formProps} />
        <strong>ALLOCATIONS:</strong>
        {renderAllocationFields()}
      </Drawer>
      <Modal
        title="Confirm Update"
        visible={editModalVisible}
        onOk={onEditConfirm}
        onCancel={onEditConfirmCancel}
      >
        <p>
          Billing Cycle entry for{" "}
          <strong style={{ fontWeight: "bold" }}> {selectedStaffName}</strong>{" "}
          for
          <strong style={{ fontWeight: "bold" }}>
            {" "}
            {dayjs(selectedEntity1?.month).format("MMM, YYYY") || ""}
          </strong>{" "}
          already exists.
        </p>
        <p>
          Click <strong style={{ fontWeight: "bold" }}>OK</strong> to modify it
          or <strong style={{ fontWeight: "bold" }}>Cancel</strong> to go back
          to list view.
        </p>
      </Modal>
      {createEditOpen && (
        <CreateEditBillingCycleComponent
          onClose={() => [setCreateEditOpen(false), onClose()]}
          visible={createEditOpen}
          selectedEntity={selectedEntity1}
          onSubmit={createEditSubmit}
        />
      )}
    </>
  );
};

export default CreateEditBillingCycleComponent;
